<template>
  <div
    class="fixed top-0 left-0 w-full h-full z-[123457698900] flex items-start justify-center bg-gray-900/50 backdrop-blur-sm p-1.5 overflow-hidden"
    @click.self="closeModal"
  >
    <div
      class="bg-white w-full max-w-[1800px] max-h-[95dvh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200"
    >
      <div
        class="px-4 py-3 border-b border-gray-100 flex justify-between items-center bg-white"
      >
        <div class="flex items-center gap-3">
          <div class="bg-blue-50 p-2 rounded-lg">
            <i
              class="fa-solid fa-cloud-showers-heavy text-blue-600 text-xl"
            ></i>
          </div>
          <h2 class="text-xl font-bold text-gray-800 tracking-tight">
            Rain Calendar
          </h2>
        </div>
        <button
          @click="closeModal"
          class="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon class="w-6 h-6" />
        </button>
      </div>

      <div class="flex flex-col lg:flex-row flex-1 overflow-hidden">
        <div
          class="flex-1 p-3 overflow-y-auto border-r border-gray-100 bg-white"
        >
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-700 capitalize">
              {{ currentMonthYear }}
            </h3>
            <div class="flex gap-2">
              <button
                :disabled="isPreviousDisabled"
                @click="previousMonth"
                class="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeftIcon class="w-5 h-5 text-gray-600" />
              </button>
              <button
                @click="nextMonth"
                class="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"
              >
                <ChevronRightIcon class="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          <div class="grid grid-cols-7 mb-2">
            <div
              v-for="day in weekdays"
              :key="day"
              class="text-center text-xs font-bold text-gray-400 uppercase tracking-wider py-2"
            >
              {{ day }}
            </div>
          </div>

          <div class="grid grid-cols-7 gap-1">
            <div
              v-for="day in calendarDays"
              :key="`${day.date}-${day.month}`"
              @click="selectDate(day)"
              class="relative md:aspect-square min-h-[50px] max-h-[100px] mx-auto my-0 min-w-[90%] flex flex-col items-center justify-center rounded-xl cursor-pointer transition-all border group"
              :class="[
                day.isOtherMonth
                  ? 'bg-gray-50/50 border-transparent text-gray-300'
                  : 'bg-white border-gray-100 text-gray-700 hover:border-blue-300 hover:shadow-sm',
                day.isToday ? 'ring-2 ring-blue-500 !border-transparent' : '',
                selectedDate && isSameDay(selectedDate, day.fullDate)
                  ? 'bg-blue-600 !text-black !border-blue-600 shadow-md scale-95'
                  : '',
              ]"
            >
              <span class="text-sm font-bold">{{ day.date }}</span>
              <i
                v-if="day.hasRain"
                class="fa-solid fa-cloud-showers-heavy text-[13px] mt-1 opacity-60"
              ></i>
            </div>
          </div>
        </div>

        <div
          class="w-full max-h-[45dvh] min-h-[35dvh] lg:min-h-none overflow-y-auto lg:max-h-none lg:w-[450px] xl:w-[750px] bg-gray-50/50 flex flex-col"
        >
          <div v-if="selectedDate" class="flex flex-col h-full">
            <div
              class="p-3 border-t md:!border-t-0 border-b border-gray-200 bg-white sticky top-0 left-0 z-[100]"
            >
              <div class="flex justify-between items-center">
                <div>
                  <h3 class="text-lg font-bold text-gray-800">
                    {{ formatSelectedDate(selectedDate) }}
                  </h3>
                  <p class="text-xs text-gray-500">
                    Events found: {{ selectedDateRains.length }}
                  </p>
                </div>
                <button
                  v-if="selectedDateRains.length > 0 && !isSelectedDateToday"
                  @click="showShuffleRainModal(true)"
                  class="flex items-center gap-2 px-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-colors"
                >
                  <i class="fa-solid fa-shuffle"></i> Shuffle
                </button>
              </div>
            </div>

            <div
              v-if="selectedDateRains.length > 0"
              class="flex-1 overflow-y-auto p-4"
            >
              <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div
                  v-for="(rain, index) in selectedDateRains"
                  :key="rain._id"
                  class="bg-white border border-gray-200 rounded-2xl p-3.5 shadow-sm relative group transition-all hover:border-blue-200"
                >
                  <div class="flex justify-between items-center mb-3">
                    <div class="flex items-center gap-2">
                      <span class="text-gray-900 font-semibold text-sm italic"
                        >#{{ index + 1 }} RAIN</span
                      >
                      <span
                        class="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase"
                        :class="{
                          'bg-amber-100 text-amber-600':
                            getStatusClass(rain) === 'coming_soon',
                          'bg-green-100 text-green-600':
                            getStatusClass(rain) === 'active',
                          'bg-pink-100 text-pink-600':
                            getStatusClass(rain) === 'ended',
                        }"
                      >
                        ({{ getStatusClass(rain).replace("_", " ") }})
                      </span>
                    </div>
                    <div
                      class="border-2 border-pink-200 text-pink-500 px-2 py-1 rounded-full text-xs font-bold bg-pink-50/30"
                    >
                      {{ formatTime(rain.start_at) }} -
                      {{ formatTime(rain.end_at) }} UTC
                    </div>
                  </div>

                  <div class="flex flex-wrap items-center gap-x-8 gap-y-3 mb-6">
                    <div class="flex items-center gap-2">
                      <div
                        class="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center"
                      >
                        <CurrencyDollarIcon class="w-4 h-4 text-green-600" />
                      </div>
                      <span class="text-xs text-gray-500 font-medium"
                        >Amount:</span
                      >
                      <span class="text-sm font-semibold text-gray-800"
                        >{{
                          parseFloat(rain.freebetcurrencies[0].amount).toFixed(
                            2,
                          )
                        }}
                        EUR</span
                      >
                    </div>

                    <div class="flex items-center gap-2">
                      <div
                        class="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center"
                      >
                        <ChatBubbleOvalLeftEllipsisIcon
                          class="w-4 h-4 text-blue-500"
                        />
                      </div>
                      <span class="text-xs text-gray-500 font-medium"
                        >Is Pushed:</span
                      >
                      <span class="text-sm font-semibold text-gray-800">{{
                        rain.is_pushed ? "Yes" : "No"
                      }}</span>
                    </div>

                    <div class="flex items-center gap-2">
                      <div
                        class="w-7 h-7 rounded-full bg-amber-50 flex items-center justify-center"
                      >
                        <RocketLaunchIcon class="w-4 h-4 text-amber-500" />
                      </div>
                      <span class="text-xs text-gray-500 font-medium"
                        >Min Cashout:</span
                      >
                      <span class="text-sm font-semibold text-gray-800"
                        >x{{ parseFloat(rain.min_cashout).toFixed(2) }}</span
                      >
                    </div>
                  </div>

                  <div class="flex flex-col gap-2 relative">
                    <div
                      class="flex items-center gap-2 text-gray-500 font-bold text-xs uppercase"
                    >
                      <UserGroupIcon class="w-4 h-4 text-cyan-500" />
                      Usage: {{ rain.players_count }}/{{ rain.max_players }}
                      players
                    </div>

                    <div
                      class="relative w-full h-1.5 bg-gray-100 rounded-full overflow-hidden"
                    >
                      <div
                        class="absolute h-full bg-green-400 rounded-full transition-all duration-700"
                        :style="{ width: getProgressPercentage(rain) + '%' }"
                      ></div>
                    </div>
                  </div>

                  <div
                    class="mt-3 pt-3 border-t border-gray-50 flex justify-between items-center"
                  >
                    <code class="text-[12px] text-slate-500"
                      >ID: {{ rain.external_id }}</code
                    >
                    <router-link
                      :to="`/freebets/${rain._id}`"
                      target="_blank"
                      class="w-9 h-9 bg-green-600 hover:bg-green-700 text-white rounded-full flex items-center justify-center transition-all shadow-lg shadow-green-100"
                    >
                      <i class="fa-solid fa-eye text-xs"></i>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="flex-1 flex flex-col items-center justify-center p-8 text-center bg-white"
            >
              <div
                class="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4"
              >
                <CalendarIcon class="w-10 h-10 text-slate-500" />
              </div>
              <h4 class="text-gray-800 font-bold">No Events</h4>
              <p class="text-sm text-gray-400 mt-1 max-w-[200px]">
                No rain events scheduled for this day.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ShuffleRain
    v-if="shuffleRainActive"
    :rainsToShuffle="selectedDateRains"
    :showShuffleRainModal="showShuffleRainModal"
  />
</template>

<script>
import {
  CalendarIcon,
  CloudIcon,
  UserGroupIcon,
  GiftIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  RocketLaunchIcon,
  ChatBubbleOvalLeftEllipsisIcon,
} from "@heroicons/vue/24/outline";
import ShuffleRain from "./ShuffleRain.vue";

export default {
  name: "RainCalendar",
  props: {
    rains: { type: Array, required: true },
    closeModal: Function,
    openDetail: Function,
  },
  components: {
    CalendarIcon,
    CloudIcon,
    UserGroupIcon,
    GiftIcon,
    CurrencyDollarIcon,
    XMarkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    RocketLaunchIcon,
    ChatBubbleOvalLeftEllipsisIcon,
    ShuffleRain,
  },
  data() {
    return {
      currentDate: new Date(),
      selectedDate: null,
      weekdays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      shuffleRainActive: false,
    };
  },
  computed: {
    isSelectedDateToday() {
      if (!this.selectedDate) return false;
      const selected = new Date(this.selectedDate);
      const today = new Date();
      selected.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return !(selected > today);
    },
    isPreviousDisabled() {
      const now = new Date();
      return (
        this.currentDate.getFullYear() === now.getFullYear() &&
        this.currentDate.getMonth() <= now.getMonth()
      );
    },
    currentMonthYear() {
      return this.currentDate.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    },
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      const startDate = new Date(firstDay);
      const dayOfWeek = startDate.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate.setDate(startDate.getDate() - daysToSubtract);

      const endDate = new Date(lastDay);
      const endDayOfWeek = endDate.getDay();
      const daysToAdd = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
      endDate.setDate(endDate.getDate() + daysToAdd);

      const days = [];
      const curr = new Date(startDate);

      while (curr <= endDate) {
        const dayRains = this.getRainsForDate(curr);
        days.push({
          date: curr.getDate(),
          month: curr.getMonth(),
          year: curr.getFullYear(),
          fullDate: new Date(curr),
          isOtherMonth: curr.getMonth() !== month,
          isToday: this.isSameDay(curr, new Date()),
          hasRain: dayRains.length > 0,
          rains: dayRains,
        });
        curr.setDate(curr.getDate() + 1);
      }
      return days;
    },
    selectedDateRains() {
      if (!this.selectedDate) return [];
      return this.getRainsForDate(this.selectedDate);
    },
  },
  methods: {
    previousMonth() {
      if (this.isPreviousDisabled) return;
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() - 1,
        1,
      );
    },
    nextMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() + 1,
        1,
      );
    },
    selectDate(day) {
      if (day.isOtherMonth) {
        this.currentDate = new Date(day.year, day.month, 1);
      }
      this.selectedDate = day.fullDate;
    },
    getRainsForDate(date) {
      return this.rains.filter((rain) => {
        const rainDate = new Date(rain.start_at);
        return this.isSameDay(rainDate, date);
      });
    },
    isSameDay(d1, d2) {
      return (
        d1.getDate() === d2.getDate() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getFullYear() === d2.getFullYear()
      );
    },
    formatSelectedDate(date) {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
    },
    formatTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    getStatusClass(rain) {
      const now = Date.now();
      const start = new Date(rain.start_at).getTime();
      const end = new Date(rain.end_at).getTime();
      if (now < start) return "coming_soon";
      if (now <= end) return "active";
      return "ended";
    },
    getProgressPercentage(rain) {
      return Math.min((rain.players_count / rain.max_players) * 100, 100);
    },
    showShuffleRainModal(val) {
      this.shuffleRainActive = val;
    },
  },
  mounted() {
    const today = this.calendarDays.find((day) => day.isToday);
    if (today) this.selectDate(today);
    document.body.style.overflow = "hidden";
  },
  beforeUnmount() {
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
.animate-in {
  animation: fadeIn 0.25s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
/* Scrollbar custom per un look pulito */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}
</style>
